import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { GetTreatmentDto, TreatmentMaterialInputDto } from 'woofwoof-api';
import * as Yup from 'yup';
import { translateKey } from 'src/locales/translate';
import AssociatedMedicinesEditor, { AssociatedMedicineValue } from 'src/sections/@dashboard/profile/components/AssociatedMedicinesEditor';
import {
  TreatmentMaterialValue,
  materialsFromServer,
  materialsToPayload,
  toAssociatedMedicineValues,
} from 'src/sections/@dashboard/profile/components/associatedMedicineStock';
import TreatmentMaterialsEditor from 'src/sections/@dashboard/profile/components/TreatmentMaterialsEditor';
import { useWarehouseStockEnabled } from 'src/sections/@dashboard/warehouse/hooks/useClinicalStockProducts';


interface CatalogFormValues {
  name: string;
  description: string;
  priceNetto: number | '';
  priceBrutto: number | '';
  vatRate: number;
  isActive: boolean;
}

export interface CatalogFormSubmitValues {
  name: string;
  description?: string;
  priceNetto?: number;
  priceBrutto?: number;
  vatRate: number;
  isActive: boolean;
  associatedMedicines: AssociatedMedicineValue[];
  /** Absent while the Warehouse is disabled: the stored defaults stay. */
  defaultMaterials?: TreatmentMaterialInputDto[];
}

const VAT_OPTIONS = [
  { value: 23, label: '23%' },
  { value: 8, label: '8%' },
  { value: 5, label: '5%' },
  { value: 0, label: '0%' },
];

const schema = Yup.object().shape({
  name: Yup.string()
    .required('Nazwa jest wymagana')
    .max(100, translateKey("hardcoded.nazwa_moze_miec_maksymalnie_100_znakow")),
  description: Yup.string().max(500, translateKey("hardcoded.opis_moze_miec_maksymalnie_500_znakow")),
  priceNetto: Yup.mixed(),
  priceBrutto: Yup.mixed(),
  vatRate: Yup.number().required().min(0).max(100),
  isActive: Yup.boolean().required(),
});

interface Props {
  isModalOpen: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  treatment?: GetTreatmentDto;
  closeModal: () => void;
  onSubmit: (values: CatalogFormSubmitValues) => void;
  onDelete: () => void;
}

export default function OrganizationTreatmentCatalogFormModal({
  isModalOpen,
  isCreating,
  isUpdating,
  isDeleting,
  treatment,
  closeModal,
  onSubmit,
  onDelete,
}: Props) {
  const isEditing = Boolean(treatment);
  const isLoading = isCreating || isUpdating;

  const [selectedMedicines, setSelectedMedicines] = useState<AssociatedMedicineValue[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<
    TreatmentMaterialValue[]
  >([]);
  const warehouseEnabled = useWarehouseStockEnabled();

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<CatalogFormValues>({
      resolver: yupResolver(schema as any),
      defaultValues: {
        name: '',
        description: '',
        priceNetto: '',
        priceBrutto: '',
        vatRate: 8,
        isActive: true,
      },
    });

  useEffect(() => {
    if (isModalOpen) {
      if (treatment) {
        reset({
          name: treatment.name,
          description: treatment.description ?? '',
          priceNetto: treatment.priceNetto ?? '',
          priceBrutto: treatment.priceBrutto ?? '',
          vatRate: treatment.vatRate ?? 8,
          isActive: treatment.isActive,
        });
        setSelectedMedicines(
          toAssociatedMedicineValues(treatment.associatedMedicines ?? []),
        );
        setSelectedMaterials(materialsFromServer(treatment.defaultMaterials));
      } else {
        reset({
          name: '',
          description: '',
          priceNetto: '',
          priceBrutto: '',
          vatRate: 8,
          isActive: true,
        });
        setSelectedMedicines([]);
        setSelectedMaterials([]);
      }
    }
  }, [isModalOpen, treatment, reset]);

  const vatRate = watch('vatRate');

  const calculateBrutto = useCallback(
    (netto: number, vat: number) =>
      Math.round(netto * (1 + vat / 100) * 100) / 100,
    [],
  );

  const calculateNetto = useCallback(
    (brutto: number, vat: number) =>
      Math.round((brutto / (1 + vat / 100)) * 100) / 100,
    [],
  );

  const handleFormSubmit = (values: CatalogFormValues) => {
    onSubmit({
      name: values.name,
      description: values.description || undefined,
      priceNetto:
        values.priceNetto !== '' ? Number(values.priceNetto) : undefined,
      priceBrutto:
        values.priceBrutto !== '' ? Number(values.priceBrutto) : undefined,
      vatRate: values.vatRate,
      isActive: values.isActive,
      associatedMedicines: selectedMedicines,
      ...(warehouseEnabled
        ? { defaultMaterials: materialsToPayload(selectedMaterials) }
        : {}),
    });
  };

  return (
    <Dialog open={isModalOpen} onClose={closeModal} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>
          {isEditing ? 'Edytuj zabieg' : 'Dodaj zabieg'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} pt={1}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Nazwa zabiegu"
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  inputProps={{ maxLength: 100 }}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={translateKey("search.ui.aboutTab")}
                  multiline
                  minRows={2}
                  maxRows={4}
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  inputProps={{ maxLength: 500 }}
                />
              )}
            />

            <Typography>Cennik</Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Controller
                  name="priceNetto"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cena netto"
                      type="number"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">{translateKey("quickBooking.currencySuffix")}</InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val);
                        if (val !== '')
                          setValue(
                            'priceBrutto',
                            calculateBrutto(Number(val), vatRate),
                          );
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name="vatRate"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>VAT</InputLabel>
                      <Select
                        {...field}
                        label="VAT"
                        onChange={(e) => {
                          const newVat = Number(e.target.value);
                          field.onChange(newVat);
                          const netto = watch('priceNetto');
                          if (netto !== '')
                            setValue(
                              'priceBrutto',
                              calculateBrutto(Number(netto), newVat),
                            );
                        }}
                      >
                        {VAT_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <Controller
                  name="priceBrutto"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cena brutto"
                      type="number"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">{translateKey("quickBooking.currencySuffix")}</InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val);
                        if (val !== '')
                          setValue(
                            'priceNetto',
                            calculateNetto(Number(val), vatRate),
                          );
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  }
                  label="Aktywny"
                />
              )}
            />

            <Divider />
            <AssociatedMedicinesEditor
              value={selectedMedicines}
              onChange={setSelectedMedicines}
              disabled={isLoading || isDeleting}
              stockEnabled
              isTemplate
            />
            {warehouseEnabled && (
              <TreatmentMaterialsEditor
                value={selectedMaterials}
                onChange={setSelectedMaterials}
                disabled={isLoading || isDeleting}
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
          <Box>
            {isEditing && (
              <LoadingButton
                variant="contained"
                color="error"
                data-testid="catalog-delete-open"
                onClick={onDelete}
                loading={isDeleting}
                disabled={isLoading}
              >{translateKey("common.remove")}</LoadingButton>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={closeModal}
              variant="outlined"
              color="inherit"
              disabled={isLoading || isDeleting}
            >{translateKey("common.cancel")}</Button>
            <LoadingButton
              type="submit"
              variant="contained"
              data-testid="catalog-save"
              loading={isLoading}
              disabled={isDeleting}
            >
              {isEditing ? translateKey("common.save") : translateKey("common.add")}
            </LoadingButton>
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
}
